import { useEffect, useState, useSyncExternalStore } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bot,
  Building2,
  CircleUser,
  Gavel,
  HeadphonesIcon,
  LayoutDashboard,
  ListTodo,
  LogOut,
  Megaphone,
  FileText,
  Receipt,
  Settings,
  Shield,
  Users,
  WifiOff,
  Wrench,
  Menu,
  X,
} from "lucide-react";
import { useAdminAuth } from "../lib/use-admin-auth";
import { authClient } from "../lib/auth-client";

function subscribeOnline(callback: () => void) {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
}

function getOnlineSnapshot() {
  return navigator.onLine;
}

function useIsOnline() {
  return useSyncExternalStore(subscribeOnline, getOnlineSnapshot);
}

function isInstalledPwa() {
  return window.matchMedia("(display-mode: standalone)").matches
    || ("standalone" in navigator && (navigator as unknown as { standalone: boolean }).standalone === true);
}

function OfflineBanner() {
  const isOnline = useIsOnline();
  const [isPwa, setIsPwa] = useState(false);

  useEffect(() => {
    setIsPwa(isInstalledPwa());
  }, []);

  if (!isPwa || isOnline) return null;

  return (
    <div className="flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-sm font-bold text-white">
      <WifiOff className="h-4 w-4" />
      Du er frakoblet
    </div>
  );
}

const logoDark = "/branding/logo-dark.svg";
const logoLight = "/branding/logo-light.svg";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/aktivitet", label: "Aktivitet", icon: Activity },
  { to: "/organisasjoner", label: "Organisasjoner", icon: Building2 },
  { to: "/brukere", label: "Brukere", icon: Users },
  { to: "/auksjoner", label: "Auksjoner", icon: Gavel },
  { to: "/oppgaver", label: "Oppgaver", icon: ListTodo },
  { to: "/dokumenter", label: "Dokumenter", icon: FileText },
  { to: "/regnskap", label: "Regnskap", icon: Receipt },
  { to: "/support", label: "Support", icon: HeadphonesIcon },
  { to: "/statistikk", label: "Statistikk", icon: BarChart3 },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/innstillinger", label: "Innstillinger", icon: Settings },
];

const devNavItems = [
  { to: "/seed-bot", label: "Seed Bot", icon: Bot },
  { to: "/vedlikehold", label: "Vedlikehold", icon: Wrench },
  { to: "/dev/dokumentmaler", label: "Dokumentmaler", icon: FileText },
];

export function AppLayout() {
  const { profile } = useAdminAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await authClient.signOut();
    navigate("/auth/login");
  };

  const displayName = profile
    ? [profile.firstName, profile.lastName].filter(Boolean).join(" ") || profile.email || "Admin"
    : "Admin";

  const initials = profile
    ? ([profile.firstName?.[0], profile.lastName?.[0]].filter(Boolean).join("") ||
      profile.email?.[0]?.toUpperCase() ||
      "A")
    : "A";

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="flex flex-col h-24 justify-center gap-1.5 px-5 border-b border-white/8">
        <img src={logoDark} alt="Forhandlerbørsen Admin" className="h-10 w-auto mr-auto" />
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-accent pl-1">
          Admin Console
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-hide">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={closeMobileMenu}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150 ${isActive
                ? "bg-white/10 text-white"
                : "text-white/55 hover:bg-white/6 hover:text-white/80"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-accent" : ""}`} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Developer tools */}
      <div className="border-t border-white/8 px-3 pt-3 pb-1">
        <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-white/25">Dev</div>
        {devNavItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={closeMobileMenu}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150 ${isActive
                ? "bg-white/10 text-white"
                : "text-white/55 hover:bg-white/6 hover:text-white/80"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-accent" : ""}`} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </div>

      {/* User footer */}
      <div className="border-t border-white/8 p-3">
        <NavLink
          to="/profil"
          onClick={closeMobileMenu}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-150 ${isActive ? "bg-white/10" : "hover:bg-white/6"
            }`
          }
        >
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold flex-shrink-0"
            style={{ background: "var(--accent)", color: "white" }}
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1 text-left">
            <div className="truncate text-sm font-medium text-white">{displayName}</div>
            <div className="truncate text-[11px] text-white/40">Administrator</div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleSignOut();
            }}
            className="flex-shrink-0 rounded-lg p-1.5 text-white/40 transition hover:bg-white/8 hover:text-white/70"
            title="Logg ut"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </NavLink>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Sidebar overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm sm:hidden transition-opacity duration-300"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-primary transform transition-transform duration-300 ease-in-out sm:hidden flex flex-col border-r border-white/8 scrollbar-hide ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <SidebarContent />
        <button
          onClick={closeMobileMenu}
          className="absolute top-6 right-4 text-white/40 hover:text-white transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </aside>

      {/* Sidebar — desktop only */}
      <aside className="hidden sm:flex w-60 flex-shrink-0 flex-col fixed inset-y-0 left-0 z-40 bg-primary border-r border-white/8 scrollbar-hide">
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col sm:ml-60 min-w-0">
        {/* Mobile header */}
        <header className="sm:hidden sticky top-0 z-40 flex h-12 items-center border-b border-border bg-background/95 px-4 backdrop-blur w-full">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="mr-3 p-1.5 -ml-1.5 text-muted-foreground hover:text-primary transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex flex-col">
            <img src={logoLight} alt="Forhandlerbørsen Admin" className="h-6 w-auto" />
            <div className="text-[7px] font-black uppercase tracking-[0.2em] text-accent pl-0.5">
              Admin Console
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <NavLink
              to="/profil"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white text-[10px] font-bold"
            >
              {initials}
            </NavLink>
          </div>
        </header>

        {/* Page content */}
        <OfflineBanner />
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export { CircleUser };
